<template>
    <div>
        <Modal v-if="proSubscription" size="xl" @close="$emit('close')">
            <template #header>
                <div class="text-gray-600">
                    <span v-if="selected !== 'cancel'">
                        Change subscription
                    </span>
                    <span v-else class="text-topol-alert-100">
                        Cancel subscription
                    </span>
                </div>
            </template>

            <template #body>
                <div class="px-6 pb-6 pt-3">
                    <div
                        class="flex flex-col rounded px-[14px] pb-2.5 pt-3 sm:flex-row"
                        :class="{
                            'bg-blue-100': selected !== 'cancel',
                            'bg-red-100': selected === 'cancel',
                        }"
                    >
                        <div
                            :class="{
                                'text-blue-600': selected !== 'cancel',
                                'text-topol-alert-100': selected === 'cancel',
                            }"
                        >
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fill-rule="evenodd"
                                    clip-rule="evenodd"
                                    d="M18 10C18 12.1217 17.1571 14.1566 15.6569 15.6569C14.1566 17.1571 12.1217 18 10 18C7.87827 18 5.84344 17.1571 4.34315 15.6569C2.84285 14.1566 2 12.1217 2 10C2 7.87827 2.84285 5.84344 4.34315 4.34315C5.84344 2.84285 7.87827 2 10 2C12.1217 2 14.1566 2.84285 15.6569 4.34315C17.1571 5.84344 18 7.87827 18 10V10ZM11 6C11 6.26522 10.8946 6.51957 10.7071 6.70711C10.5196 6.89464 10.2652 7 10 7C9.73478 7 9.48043 6.89464 9.29289 6.70711C9.10536 6.51957 9 6.26522 9 6C9 5.73478 9.10536 5.48043 9.29289 5.29289C9.48043 5.10536 9.73478 5 10 5C10.2652 5 10.5196 5.10536 10.7071 5.29289C10.8946 5.48043 11 5.73478 11 6ZM9 9C8.73478 9 8.48043 9.10536 8.29289 9.29289C8.10536 9.48043 8 9.73478 8 10C8 10.2652 8.10536 10.5196 8.29289 10.7071C8.48043 10.8946 8.73478 11 9 11V14C9 14.2652 9.10536 14.5196 9.29289 14.7071C9.48043 14.8946 9.73478 15 10 15H11C11.2652 15 11.5196 14.8946 11.7071 14.7071C11.8946 14.5196 12 14.2652 12 14C12 13.7348 11.8946 13.4804 11.7071 13.2929C11.5196 13.1054 11.2652 13 11 13V10C11 9.73478 10.8946 9.48043 10.7071 9.29289C10.5196 9.10536 10.2652 9 10 9H9Z"
                                    fill="currentColor"
                                />
                            </svg>
                        </div>
                        <div
                            class="mt-2 text-sm sm:ml-[14px] sm:mt-0"
                            :class="{
                                'text-blue-600': selected !== 'cancel',
                                'text-red-600': selected === 'cancel',
                            }"
                        >
                            You are currently using
                            <span
                                :class="{
                                    'text-blue-700': selected !== 'cancel',
                                    'text-red-700': selected === 'cancel',
                                }"
                            >
                                {{
                                    proSubscription.period === 1
                                        ? 'Month-to-month'
                                        : 'Annual'
                                }}
                                subscription plan for Topol
                                {{ team.personal_team ? 'PRO' : 'Team' }} </span
                            >, including
                            <span v-if="!team.personal_team">
                                {{ proSubscription.totalPaidSeats }} pre-paid
                                member seats.
                            </span>
                            <span v-else> 1 member seat. </span>
                            <span
                                v-if="
                                    !proSubscription.canceled_at &&
                                    proSubscription.next_billing_date
                                "
                            >
                                Your plan
                                <span
                                    v-if="
                                        new Date(
                                            proSubscription.next_billing_date
                                        ) > new Date()
                                    "
                                    >will renew on</span
                                >
                                <span v-else>was about to renew on</span>
                                {{
                                    format(
                                        new Date(
                                            proSubscription.next_billing_date
                                        ),
                                        'd MMMM, yyyy'
                                    )
                                }}.
                            </span>
                        </div>
                    </div>
                    <div
                        class="mt-5 rounded-md border border-gray-200 bg-gray-50 px-6 pb-6 pt-4"
                    >
                        <SelectInput
                            ref="selectInput"
                            v-model="selected"
                            :data="proPlans()"
                            label="Choose your subscription"
                            placeholder="Choose subscription"
                            class="max-w-[400px]"
                            @update:modelValue="getPreparedSubscription"
                        />

                        <div
                            v-if="selected === proSubscription.plan?.uuid"
                            class="mt-3"
                        >
                            <div
                                class="text-2xl leading-normal tracking-tighter text-gray-800"
                            >
                                <span
                                    v-if="proSubscription?.amount_with_tax"
                                    class="font-bold"
                                >
                                    ${{
                                        proSubscription?.amount_with_tax
                                            ? proSubscription.amount_with_tax /
                                              100
                                            : 0
                                    }}
                                </span>
                                <span
                                    >/{{
                                        proSubscription.period === 1
                                            ? 'month'
                                            : 'year'
                                    }}
                                </span>
                            </div>
                            <div
                                v-if="!team.personal_team"
                                class="text-sm text-gray-500"
                            >
                                ${{
                                    getMonthlyPriceForUsersForSubscriptionPlan(
                                        proSubscription.subscription_plan_uuid
                                    )
                                }}
                                per seat per 1 month<br />
                                for {{ proSubscription.totalPaidSeats }} team
                                seats (3 included)
                            </div>

                            <div v-else class="text-sm text-gray-500">
                                for your personal account
                            </div>

                            <div
                                v-if="
                                    !proSubscription.canceled_at &&
                                    proSubscription.next_billing_date
                                "
                                class="mt-[18px] text-sm"
                            >
                                <div class="text-gray-500">
                                    {{
                                        proSubscription.period === 1
                                            ? 'Month-to-month'
                                            : 'Annual'
                                    }}
                                    plan
                                    <span
                                        v-if="
                                            new Date(
                                                proSubscription.next_billing_date
                                            ) > new Date()
                                        "
                                    >
                                        renews on
                                    </span>
                                    <span v-else
                                        >was scheduled to renew on</span
                                    >
                                </div>

                                <div class="font-medium text-gray-800">
                                    {{
                                        format(
                                            new Date(
                                                proSubscription.next_billing_date
                                            ),
                                            'd MMMM, yyyy'
                                        )
                                    }}
                                </div>
                            </div>
                            <div
                                v-else-if="proSubscription.canceled_at"
                                class="mt-[18px] text-sm text-gray-500"
                            >
                                <div>
                                    Subscription canceled, will be active until
                                </div>
                                <div class="font-medium text-gray-800">
                                    {{
                                        format(
                                            new Date(
                                                proSubscription.canceled_at
                                            ),
                                            'd MMMM, yyyy'
                                        )
                                    }}
                                </div>
                            </div>
                        </div>

                        <div v-if="proSubscription.plan?.uuid !== selected">
                            <div class="mt-4 text-sm text-gray-500">
                                <span
                                    v-if="
                                        selected !== 'cancel' &&
                                        preparedSubscription?.subscription_plan.uuid.includes(
                                            'yearly'
                                        )
                                    "
                                >
                                    When switching to annual billing,
                                    <span class="font-medium text-gray-800"
                                        >you will be able to use Topol PRO
                                        features
                                        <span v-if="!team.personal_team">
                                            for
                                            {{ proSubscription.totalPaidSeats }}
                                            member seats
                                        </span>
                                        starting immediately.</span
                                    >
                                    You will be billed on annual basis.
                                </span>
                                <span
                                    v-if="
                                        selected !== 'cancel' &&
                                        preparedSubscription?.subscription_plan.uuid.includes(
                                            'monthly'
                                        ) &&
                                        proSubscription.next_billing_date &&
                                        new Date(
                                            proSubscription.next_billing_date
                                        ) > new Date()
                                    "
                                    >When switching to month-to-month billing,
                                    <span class="font-medium text-gray-800"
                                        >you will be able to use Topol PRO
                                        features
                                        <span v-if="!team.personal_team">
                                            for
                                            {{ proSubscription.totalPaidSeats }}
                                            member seats
                                        </span>

                                        without change until
                                        {{
                                            format(
                                                new Date(
                                                    proSubscription.next_billing_date
                                                ),
                                                'd MMMM, yyyy'
                                            )
                                        }}
                                        .</span
                                    >

                                    After this date you will be billed on
                                    month-to-month basis.</span
                                >
                                <span
                                    v-if="
                                        selected === 'cancel' &&
                                        proSubscription.next_billing_date &&
                                        new Date(
                                            proSubscription.next_billing_date
                                        ) > new Date()
                                    "
                                >
                                    When switching to free plan,
                                    <span class="font-medium text-gray-800"
                                        >you will be able to use Topol PRO
                                        features
                                        <span v-if="!team.personal_team">
                                            for
                                            {{ proSubscription.totalPaidSeats }}
                                            member seats
                                        </span>
                                        until
                                        {{
                                            format(
                                                new Date(
                                                    proSubscription.next_billing_date
                                                ),
                                                'd MMMM, yyyy'
                                            )
                                        }}.</span
                                    >
                                    After this date you will be switched to FREE
                                    plan without PRO and Team features.</span
                                >

                                <p class="mt-2">
                                    Note that after your subscription is
                                    canceled, you will lose access to the images
                                    saved in our storage.
                                </p>
                            </div>
                            <div
                                v-if="
                                    selected !== 'cancel' &&
                                    preparedSubscription
                                "
                            >
                                <div
                                    class="mt-[22px] flex items-start gap-2 text-2xl leading-normal tracking-tighter text-gray-800"
                                >
                                    <div>
                                        <span
                                            class="font-bold text-gray-500 line-through"
                                            >${{
                                                preparedSubscription.total_without_sale /
                                                100
                                            }}</span
                                        >
                                    </div>
                                    <div>
                                        <span class="font-bold"
                                            >${{
                                                preparedSubscription.total / 100
                                            }}</span
                                        >
                                        <span
                                            >/{{
                                                preparedSubscription
                                                    .subscription_plan
                                                    .period === 1
                                                    ? 'month'
                                                    : 'year'
                                            }}
                                        </span>
                                    </div>

                                    <!-- <div
                                        v-if="preparedSubscription.subscription_plan.period === 12"
                                        class="inline-flex rounded-md bg-black px-3 py-[3px] text-xs font-medium text-white uppercase font-bold tracking-wide"
                                    >
                                        Black friday deal
                                    </div> -->
                                </div>
                                <div
                                    v-if="!team.personal_team"
                                    class="text-sm text-gray-500"
                                >
                                    ${{
                                        getMonthlyPriceForUsersForSubscriptionPlan(
                                            preparedSubscription
                                                .subscription_plan.uuid
                                        )
                                    }}
                                    per seat per 1 month<br />
                                    for
                                    {{
                                        preparedSubscription.additional_users_to_pay_count +
                                        3
                                    }}
                                    team seats (3 included)
                                </div>

                                <div v-else class="text-sm text-gray-500">
                                    for your personal account
                                </div>

                                <div class="mt-5 text-sm text-gray-500">
                                    <div>
                                        {{
                                            preparedSubscription.period === 1
                                                ? 'Month-to-month'
                                                : 'Annual'
                                        }}
                                        billing starts on
                                    </div>
                                    <div
                                        v-if="
                                            proSubscription.next_billing_date &&
                                            new Date(
                                                proSubscription.next_billing_date
                                            ) > new Date()
                                        "
                                        class="font-medium text-gray-800"
                                    >
                                        {{
                                            format(
                                                new Date(
                                                    proSubscription.next_billing_date
                                                ),
                                                'd MMMM, yyyy'
                                            )
                                        }}
                                    </div>
                                    <div
                                        v-else
                                        class="font-medium text-gray-800"
                                    >
                                        {{ format(new Date(), 'd MMMM, yyyy') }}
                                    </div>
                                </div>
                            </div>
                            <div v-if="selected === 'cancel'">
                                <div
                                    class="mt-[22px] text-2xl font-bold leading-normal tracking-tighter text-gray-800"
                                >
                                    Topol FREE
                                </div>
                                <div class="text-sm text-gray-500">
                                    1 user, without PRO and Team features.
                                </div>
                                <div class="mt-5 text-sm text-gray-500">
                                    <div>Free plan starts on</div>
                                    <div
                                        v-if="
                                            proSubscription.next_billing_date &&
                                            new Date(
                                                proSubscription.next_billing_date
                                            ) > new Date()
                                        "
                                        class="font-medium text-gray-800"
                                    >
                                        {{
                                            format(
                                                new Date(
                                                    proSubscription.next_billing_date
                                                ),
                                                'd MMMM, yyyy'
                                            )
                                        }}
                                    </div>

                                    <div
                                        v-else
                                        class="font-medium text-gray-800"
                                    >
                                        {{ format(new Date(), 'd MMMM, yyyy') }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="mt-5">
                        <CheckboxInput
                            v-model:checked="confirmation"
                            size="md"
                            :disabled="
                                proSubscription.plan?.uuid === selected &&
                                !proSubscription.canceled_at
                            "
                            name="confirm"
                            :label="
                                selected !== 'cancel'
                                    ? 'Confirm that you want to change your subscription'
                                    : 'Confirm that you want to CANCEL your subscription'
                            "
                        />
                    </div>
                </div>
            </template>

            <template #footer>
                <div class="flex justify-between border-t border-gray-200 p-6">
                    <AtomicButton
                        color="white"
                        size="md"
                        @click="$emit('close')"
                    >
                        Cancel
                    </AtomicButton>

                    <AtomicButton
                        :disabled="
                            (proSubscription.plan?.uuid === selected &&
                                !proSubscription.canceled_at) ||
                            (selected === 'cancel' &&
                                proSubscription.canceled_at !== null) ||
                            !confirmation ||
                            loading ||
                            (!preparedSubscription &&
                                selected !== 'cancel' &&
                                selected !== proSubscription.plan?.uuid)
                        "
                        size="md"
                        :color="selected === 'cancel' ? 'red' : 'yellow'"
                        @click="handleChange"
                    >
                        <span
                            v-if="
                                proSubscription.canceled_at !== null &&
                                selected === proSubscription.plan?.uuid
                            "
                        >
                            Resume
                        </span>
                        <span
                            v-else-if="
                                selected !== 'cancel' &&
                                selected !== proSubscription.plan?.uuid
                            "
                        >
                            Change subscription
                        </span>
                        <span v-else>
                            <span class="sm:hidden">Cancel</span>
                            <span class="hidden sm:block">
                                Cancel subscription and switch to FREE plan
                            </span>
                        </span>
                    </AtomicButton>
                </div>
            </template>
        </Modal>
    </div>
</template>
<script lang="ts" setup>
import ISubscription from '@/types/Subscription/ISubscription';
import { format } from 'date-fns';
import { computed, ref } from 'vue';
import { route } from 'ziggy-js';
import axios from 'axios';
import IApiResponse from '@/types/IApiResponse';
import PreparedSubscription from '@/types/Subscription/PreparedSubscription';
import { router } from '@inertiajs/vue3';
import { create } from '@/composables/useNotifications';
import {
    ICreditCardPaymentMethod,
    IPaymentMethod,
} from '@/types/Subscription/IPaymentMethod';
import { usePage } from '@inertiajs/vue3';
import { ITopolPageProps } from '@/types/TopolPageProps/TopolPageProps';
import { useBraintreeClient } from '@/composables/useBraintree';
import SubscriptionPlan from '@/types/Subscription/SubscriptionPlan';
import useSubscriptionPlans from '../composables/useSubscriptionPlans';
import { useGtm } from '@/composables/useGtm';

const emit = defineEmits(['close', 'cancel']);

const props = defineProps<{
    proSubscription: ISubscription | null;
    selectedCard?: IPaymentMethod;
    subscriptionPlans: SubscriptionPlan[];
}>();

const team = computed(() => usePage<ITopolPageProps>().props.team);
const { teamFeatureDisabled } = useFeatures();
const { pushEvent } = useGtm();

const { verifyCardByToken, createClient } = useBraintreeClient('pro');
const isStripeEnabled = !teamFeatureDisabled('stripe');

const { getMonthlyPriceForUsersForSubscriptionPlan } = useSubscriptionPlans(
    props.subscriptionPlans
);

const proPlans = () => {
    const plans: { title: string; value: number | string }[] =
        props.subscriptionPlans
            .filter((plan) =>
                team.value.personal_team
                    ? !plan.uuid.includes('team')
                    : plan.uuid.includes('team')
            )
            .map((plan) => ({
                title:
                    plan.period === 1
                        ? `Topol ${plan.name}`
                        : `Topol ${plan.name}`,
                value: plan.uuid,
            }));

    plans.push({
        title: 'Topol FREE - Free plan',
        value: 'cancel',
    });

    return plans;
};

const confirmation = ref(false);

const selected = ref<number | string | undefined>(
    props.proSubscription?.plan?.uuid
);
const selectInput = ref();
const preparedSubscription = ref();
const loading = ref(false);

const handleChange = async () => {
    try {
        loading.value = true;
        if (
            selected.value !== 'cancel' &&
            selected.value !== props.proSubscription?.plan?.uuid
        ) {
            await changeSubscription();
        }
        if (selected.value === 'cancel') {
            await cancelSubscription();
        }
        if (
            selected.value === props.proSubscription?.plan?.uuid &&
            props.proSubscription?.canceled_at
        ) {
            await resumeSubscription();
        }
    } catch (error) {
        create({
            title: 'Subscription not changed',
            text: 'There was an error changing your subscription. If the problem persists, please contact support at support@topol.io.',
            type: 'error',
        });
    } finally {
        loading.value = false;
    }
};

const changeSubscription = async () => {
    if (props.selectedCard?.token === undefined) {
        throw new Error('Card not selected');
    }

    if (props.proSubscription === null) {
        throw new Error('Subscription not found');
    }

    const changeRoute = isStripeEnabled
        ? route('stripe.subscriptions.update', {
              subscription: props.proSubscription.id,
          })
        : route('subscriptions.switch', {
              subscription: props.proSubscription.id,
          });

    const method = isStripeEnabled ? 'put' : 'post';

    let verifyCardResponse = undefined;
    if (!isStripeEnabled) {
        await createClient({ enableHostedFields: false });

        if (
            (props.selectedCard as ICreditCardPaymentMethod).bin !== undefined
        ) {
            verifyCardResponse = await verifyCardByToken({
                token: props.selectedCard.token,
                amount: preparedSubscription.value.total,
            });
        }
    }

    return new Promise((resolve, reject) => {
        router[method](
            changeRoute,
            {
                subscription_plan_uuid: selected.value,
                nonce: verifyCardResponse ? verifyCardResponse.nonce : null,
            },
            {
                preserveState: false,
                onSuccess: (response) => {
                    create({
                        title: 'Subscription switched',
                        text: 'Your subscription has been successfully updated.',
                        type: 'success',
                    });
                    emit('close');
                    resolve(response);
                },
                onError: (error) => {
                    create({
                        title: 'Subscription not switched',
                        text:
                            error.message ??
                            'There was an error updating your subscription.',
                        type: 'error',
                    });
                    reject(error);
                },
            }
        );
    });
};

const cancelSubscription = () => {
    return new Promise((resolve, reject) => {
        if (props.proSubscription === null) {
            throw new Error('Subscription not found');
        }

        const cancelRoute = isStripeEnabled
            ? route('stripe.subscriptions.cancel', {
                  subscription: props.proSubscription.id,
              })
            : route('subscriptions.cancel', {
                  subscription: props.proSubscription.id,
              });

        router.post(
            cancelRoute,
            {},
            {
                preserveScroll: true,
                onSuccess: (response) => {
                    create({
                        title: 'Subscription canceled',
                        text: 'Your PRO subscription has been successfully canceled.',
                        type: 'success',
                    });
                    emit('cancel');
                    resolve(response);
                },
                onError: (response) => {
                    create({
                        title: 'Subscription not canceled',
                        text: 'There was an error canceling your PRO subscription.',
                        type: 'error',
                    });
                    reject(response);
                },
            }
        );
    });
};

const resumeSubscription = () => {
    return new Promise((resolve, reject) => {
        if (props.proSubscription === null) {
            throw new Error('Subscription not found');
        }

        if (isStripeEnabled) {
            throw new Error('You cannot resume your subscription');
        }

        router.post(
            route('subscriptions.resume', {
                subscription: props.proSubscription.id,
            }),
            {},
            {
                preserveScroll: true,
                onSuccess: (response) => {
                    create({
                        title: 'Subscription extended',
                        text: 'Your PRO subscription has been successfully extended.',
                        type: 'success',
                    });
                    pushEvent('topol_purchase_renewal', {
                        type: 'pro',
                        amountPaid: preparedSubscription.value.total / 100,
                        planType:
                            preparedSubscription.value.subscription_plan
                                .period === 1
                                ? 'monthly'
                                : 'yearly',
                        currency: 'USD',
                    });

                    emit('close');
                    resolve(response);
                },
                onError: (response) => {
                    create({
                        title: 'Subscription not extended',
                        text: 'There was an error extending your PRO subscription.',
                        type: 'error',
                    });
                    reject(response);
                },
            }
        );
    });
};

const getPreparedSubscription = async () => {
    if (
        selected.value !== 'cancel' &&
        selected.value !== props.proSubscription?.plan?.uuid
    ) {
        try {
            const res = await axios.get<IApiResponse<PreparedSubscription>>(
                route('subscriptions.prepare', {
                    subscription_plan_uuid: selected.value,
                })
            );
            preparedSubscription.value = res.data.data;
        } catch (error) {
            create({
                title: 'Subscription not prepared',
                text: 'There was an error preparing your subscription.',
                type: 'error',
            });
        }
    }
};
</script>
